import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AlertController,
  LoadingController,
  ToastController,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { extractBackendMessage } from './api-error.util';

export interface AlertOptions {
  headerKey: string;
  headerParams?: Record<string, any>;
  subHeaderKey?: string;
  messageKey?: string;
  messageParams?: Record<string, any>;
  rawMessage?: string;
  buttonKey?: string;
  cssClass?: string;
}

export interface ToastOptions {
  color?: string;
  icon?: string;
  duration?: number;
  position?: 'top' | 'bottom' | 'middle';
  params?: Record<string, any>;
}

/**
 * Helpers para los overlays más repetidos del proyecto (loading / alert /
 * toast con traducción instantánea). Colapsa el boilerplate
 * `create() → present() → dismiss()` + `instant(...) as string` que estaba
 * copiado en ~12 sitios (login, config, setting, page.base, selectables…).
 */
@Injectable({
  providedIn: 'root',
})
export class UiFeedbackService {
  private loadingController = inject(LoadingController);
  private alertController = inject(AlertController);
  private toastController = inject(ToastController);
  private translateService = inject(TranslateService);

  /** Crea y presenta un loading; el llamador lo cierra con `await loading.dismiss()`. */
  async showLoading(): Promise<HTMLIonLoadingElement> {
    const loading = await this.loadingController.create();
    await loading.present();
    return loading;
  }

  /** Alert simple de un botón (OK por defecto), con claves i18n. */
  async showAlert(options: AlertOptions): Promise<void> {
    const alert = await this.alertController.create({
      cssClass: options.cssClass,
      header: this.translateService.instant(
        options.headerKey,
        options.headerParams,
      ) as string,
      subHeader: options.subHeaderKey
        ? (this.translateService.instant(options.subHeaderKey) as string)
        : undefined,
      message: options.rawMessage
        ? options.rawMessage
        : (this.translateService.instant(
            options.messageKey,
            options.messageParams,
          ) as string),
      buttons: [
        this.translateService.instant(
          options.buttonKey ?? 'COMMON.OK',
        ) as string,
      ],
    });
    await alert.present();
  }

  /** Confirmación de dos botones (Cancel / action). Retorna true si confirma. */
  async showConfirm(options: {
    headerKey: string;
    messageKey: string;
    messageParams?: Record<string, any>;
    confirmKey?: string;
    cancelKey?: string;
  }): Promise<boolean> {
    return new Promise(resolve => {
      this.alertController
        .create({
          header: this.translateService.instant(options.headerKey) as string,
          message: this.translateService.instant(options.messageKey, options.messageParams) as string,
          buttons: [
            {
              text: this.translateService.instant(options.cancelKey ?? 'COMMON.CANCEL') as string,
              role: 'cancel',
              handler: () => resolve(false),
            },
            {
              text: this.translateService.instant(options.confirmKey ?? 'COMMON.DELETE') as string,
              role: 'destructive',
              handler: () => resolve(true),
            },
          ],
        })
        .then(alert => alert.present());
    });
  }

  async showToast(
    messageKey: string,
    options: ToastOptions = {},
  ): Promise<void> {
    const toast = await this.toastController.create({
      message: this.translateService.instant(
        messageKey,
        options.params,
      ) as string,
      duration: options.duration ?? 3000,
      position: options.position ?? 'bottom',
      color: options.color,
      icon: options.icon,
    });
    await toast.present();
  }

  /**
   * Alert de error con preferencia al mensaje descriptivo del backend
   * (`{ error: "<texto>", error_code: "<opcional>" }`). Si no hay mensaje útil
   * extraído, cae al fallback i18n (comportamiento previo).
   */
  async showError(
    error: HttpErrorResponse,
    fallback: {
      headerKey: string;
      messageKey: string;
      messageParams?: Record<string, any>;
    },
  ): Promise<void> {
    const backendMsg = extractBackendMessage(error);
    if (backendMsg) {
      await this.showAlert({
        headerKey: fallback.headerKey,
        rawMessage: backendMsg,
      });
    } else {
      await this.showAlert({
        headerKey: fallback.headerKey,
        messageKey: fallback.messageKey,
        messageParams: fallback.messageParams,
      });
    }
  }
}
